import { FilterViewer, offeredFilters, SearchFilter } from "./filterRegistry";
import { SearchToken, tokenAt } from "./searchTokens";

/**
 * What the caret is editing. A key context offers filter keys and replaces
 * `[from, to)` with `key:`; a value context offers the filter's values and
 * replaces the value part `[from, to)`. `token` is undefined between tokens.
 */
export type SuggestionContext =
  | {
      kind: "key";
      token?: SearchToken;
      from: number;
      to: number;
      query: string;
    }
  | {
      kind: "value";
      token: SearchToken;
      filter: SearchFilter;
      from: number;
      to: number;
      /** The whole value part under the caret. */
      query: string;
      /** The token's other values. */
      taken: string[];
    };

export function suggestionContext(
  raw: string,
  tokens: SearchToken[],
  caret: number
): SuggestionContext | null {
  const token = tokenAt(tokens, caret);
  if (!token) return { kind: "key", from: caret, to: caret, query: "" };

  if (token.key !== null && caret > token.keyTo) {
    if (!token.filter || token.filter.options === "none") return null;
    const part = token.parts.find((p) => p.from <= caret && caret <= p.to) ?? token.parts[0];
    return {
      kind: "value",
      token,
      filter: token.filter,
      from: part.from,
      to: part.to,
      query: part.value,
      taken: token.parts.filter((p) => p !== part && p.value).map((p) => p.value),
    };
  }

  if (token.key === null && token.quoted) return null;
  const keyFrom =
    token.key !== null ? token.keyFrom : token.from + (raw[token.from] === "-" ? 1 : 0);
  if (caret < keyFrom) return null;
  return {
    kind: "key",
    token,
    from: keyFrom,
    // A typed key is swapped whole, with its colon; in free text only the text before the caret.
    to: token.key !== null ? token.keyTo + 1 : caret,
    query: raw.slice(keyFrom, caret),
  };
}

/** Offered filters whose key, alias or label starts with the typed text. */
export function matchingFilters(viewer: FilterViewer, query: string): SearchFilter[] {
  const needle = query.toLowerCase();
  return offeredFilters(viewer).filter((filter) =>
    [filter.key, ...filter.aliases, filter.label].some((name) =>
      name.toLowerCase().startsWith(needle)
    )
  );
}
